import React from 'react';
import type { ThemeName } from 'config/branding/themes';
import PortableText from 'libs/sanity/components/PortableText';
import Text from 'components/Text';
import SanityBackgroundMediaSwitch from '@fogia/core-web/components/SanityMedia/SanityBackgroundMediaSwitch';
import { getLayoutImageSizes } from '@fogia/core-web/utils/getLayoutImageSizes';
import Link from '@fogia/core-web/components/Link';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { getLinkUrlFromLinkContent } from '@fogia/core-web/utils/internalRefToSlug';
import type {
    ButtonLinkArrayComponent,
    CallToActionContent,
    MediaItemResponsiveComponent,
} from 'libs/sanity/types/generated';
import styled, { type CSSProps } from 'libs/styled';
import Button from 'components/Button';
import BlockGridWrapper from 'components/wrappers/BlockGridWrapper';
import ContentGradientOverlay from 'components/ContentGradientOverlay';
import { above } from '@fogia/core-web/utils/mediaqueries';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';

export interface CallToActionBlockComponentProps {
    content?: CallToActionContent & {
        tag?: string;
    };
    media?: MediaItemResponsiveComponent;
    /** Fade overlay for content readability */
    contentGradient?: boolean;
    /** Layout configuration determined by the layout context where the block is rendered */
    layout?: 'fullWidth' | 'halfWidth' | 'quarterWidth';
    /** Theme for semantic colors (sets data-theme on root) */
    theme?: ThemeName;
    imageLoading?: 'eager' | 'lazy';
}

const CTAContainer = styled('div')<CSSProps>`
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    aspect-ratio: 9 / 16;
    color: var(--ui-primary);

    &[data-layout='fullWidth'] {
        /* Pulls the next section up 1px to hide a thin gap that videos can leave between a fullWidth CTA and the Footer at certain screen widths. */
        margin-bottom: -1px;

        ${above['tablet.lg']} {
            aspect-ratio: 16 / 9;
        }
    }

    &[data-layout='quarterWidth'] {
        aspect-ratio: 4 / 5;
    }

    &[data-layout='halfWidth'] {
        aspect-ratio: 4 / 5;

        ${above['tablet.lg']} {
            aspect-ratio: 8 / 9;
        }
    }
`;

const MediaWrapper = styled('div')`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
`;

const StyledBlockGridWrapper = styled(BlockGridWrapper, {
    shouldForwardProp: (prop) => prop !== '$hasLargeHeading',
})<{ $hasLargeHeading?: boolean }>`
    grid-template-rows: minmax(0, 1fr) auto minmax(min-content, 1fr);
    align-content: initial;

    &[data-layout='fullWidth'] {
        ${above['tablet.lg']} {
            ${({ $hasLargeHeading }) =>
                !$hasLargeHeading &&
                `
                grid-template-rows: auto;
                align-content: center;
            `}
        }
    }
`;

const TagWrapper = styled(Text)`
    display: block;
    position: absolute;
    top: var(--grid-padding, 10px);
    left: var(--grid-padding, 10px);
    z-index: 2;

    &[data-layout='quarterWidth'] {
        display: none;
    }
`;

const HeadingButtonsWrapper = styled('div', {
    shouldForwardProp: (prop) => prop !== '$hasLargeHeading',
})<{ $hasLargeHeading?: boolean }>`
    display: flex;
    flex-direction: column;
    z-index: 1;
    grid-column: 1 / -1;
    grid-row: 2;
    align-self: center;
    min-height: min-content;

    &[data-layout='fullWidth'] {
        ${above['tablet.lg']} {
            ${({ $hasLargeHeading }) =>
                $hasLargeHeading
                    ? `grid-column: 1 / 13;`
                    : `
                grid-column: 1 / 7;
                grid-row: 1;
                align-self: stretch;
                justify-content: flex-start;
            `}
        }
    }

    &[data-layout='halfWidth'] {
        ${above['tablet.lg']} {
            grid-column: 1 / 13;
        }
    }

    &[data-layout='quarterWidth'] {
        ${above['tablet.lg']} {
            grid-column: 1 / 19;
        }
    }
`;

const ButtonsWrapper = styled('div')`
    display: flex;
    flex-direction: column;
`;

const RichTextWrapper = styled('div', {
    shouldForwardProp: (prop) => prop !== '$hasLargeHeading',
})<{ $hasLargeHeading?: boolean }>`
    display: flex;
    flex-direction: column;
    overflow: auto;
    min-height: 0;
    grid-column: 1 / -1;
    grid-row: 3;
    justify-content: flex-end;

    &[data-layout='fullWidth'] {
        ${above['tablet.lg']} {
            ${({ $hasLargeHeading }) =>
                $hasLargeHeading
                    ? `grid-column: 1 / 7;`
                    : `
                grid-column: 7 / 13;
                grid-row: 1;
                align-self: stretch;
                justify-content: flex-start;
            `}
        }
    }

    &[data-layout='halfWidth'] {
        ${above['tablet.lg']} {
            grid-column: 1 / 13;
        }
    }

    &[data-layout='quarterWidth'] {
        display: none;
    }
`;

const CallToActionBlockComponent = ({
    content,
    media,
    contentGradient: contentGradientEnabled = false,
    layout = 'fullWidth',
    theme = 'dark',
    imageLoading = 'lazy',
}: CallToActionBlockComponentProps) => {
    if (!content) {
        return null;
    }

    const { tag, heading, richText, buttons, paragraphLayout } = content;
    const { text: headingText = '', level: headingLevel = 'h2' } = heading ?? {};
    const hasRichText = Array.isArray(richText) ? richText.length > 0 : !!richText;
    const isMobile = useBreakpoint({ to: 'tablet.lg' });

    const pageReferences = useAppSelector((state) => state.page?.data?.references) ?? {};
    const singleButtonWithLink =
        buttons && Array.isArray(buttons) && buttons.length === 1 && buttons[0].type === 'buttonLinkComponent'
            ? buttons[0]
            : null;
    const containerUrl = singleButtonWithLink
        ? getLinkUrlFromLinkContent(singleButtonWithLink, { ...pageReferences })
        : undefined;
    const containerTarget = singleButtonWithLink?.target;

    // Determine heading typography based on paragraphLayout
    const hasLargeHeading = paragraphLayout === 'below';

    const headingTypography: string | Array<string | null> =
        layout === 'quarterWidth'
            ? ['Primary/Mobile/13_100_510_-1', null, null, null, 'Primary/20_120_510_-3']
            : hasLargeHeading
              ? ['Primary/Mobile/18_120_510_-3', null, null, null, 'Primary/30_100_510_-5']
              : ['Primary/24_110_510_-3', null, null, null, 'Primary/20_120_510_-3'];

    const buttonSize = layout === 'quarterWidth' && isMobile ? ('small' as const) : ('large' as const);

    const mediaMode = layout === 'fullWidth' ? 'auto' : 'portrait';

    return (
        <CTAContainer
            as={containerUrl ? Link : undefined}
            to={containerUrl ?? undefined}
            target={containerTarget ?? undefined}
            data-layout={layout}
            data-theme={theme}
        >
            {media && (
                <MediaWrapper>
                    <SanityBackgroundMediaSwitch
                        media={media}
                        breakpoint="tablet.lg"
                        mode={mediaMode}
                        sizes={getLayoutImageSizes(layout)}
                        loading={imageLoading}
                    >
                        {contentGradientEnabled && <ContentGradientOverlay />}
                    </SanityBackgroundMediaSwitch>
                </MediaWrapper>
            )}
            <StyledBlockGridWrapper
                $hasLargeHeading={hasLargeHeading}
                data-layout={layout}
                alignItems="stretch"
                py="var(--grid-padding, 10px)"
            >
                {tag && (
                    <TagWrapper
                        $as="span"
                        data-layout={layout}
                        typography={
                            layout === 'fullWidth' || layout === 'halfWidth'
                                ? ['Primary/Mobile/13_100_510_-1', null, null, null, 'Primary/15_120_510_-1']
                                : undefined
                        }
                    >
                        {tag}
                    </TagWrapper>
                )}
                <HeadingButtonsWrapper $hasLargeHeading={hasLargeHeading} data-layout={layout}>
                    {headingText && (
                        <Text $as={headingLevel} typography={headingTypography}>
                            {headingText}
                        </Text>
                    )}
                    {buttons && Array.isArray(buttons) && buttons.length > 0 && (
                        <ButtonsWrapper>
                            {buttons.map((button: ButtonLinkArrayComponent[number]) => {
                                const { type, key, label } = button;
                                if (type !== 'buttonLinkComponent') {
                                    return null;
                                }
                                return (
                                    <Button
                                        key={key}
                                        theme="textButtonUnderline"
                                        as={containerUrl ? 'span' : undefined}
                                        sanityLink={containerUrl ? undefined : button}
                                        size={buttonSize}
                                    >
                                        {label}
                                    </Button>
                                );
                            })}
                        </ButtonsWrapper>
                    )}
                </HeadingButtonsWrapper>
                {hasRichText && (
                    <RichTextWrapper $hasLargeHeading={hasLargeHeading} data-layout={layout}>
                        <PortableText
                            value={richText}
                            typography={['Primary/Mobile/13_100_510_-1', null, null, null, 'Primary/15_120_510_-1']}
                        />
                    </RichTextWrapper>
                )}
            </StyledBlockGridWrapper>
        </CTAContainer>
    );
};

export default CallToActionBlockComponent;
